import { useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { GenericTable } from "../../../Shared/components/Table/GenericTable";
import { TableActionButtons } from "../../../Shared/components/Table/TableActionButtons";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { IActivity } from "../models/Activity";
import { ActivityService } from "../services/ActivityService";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { useNavigate } from "react-router-dom";

export const Activities = () => {
  const navigate = useNavigate();

  const message = "Loading Activity Data, Please Wait";
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<IActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();

  const findActivities = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: globalFilter,
      };
      setIsLoading(true);
      const response = await ActivityService.getAll(pageOptions);
      setIsLoading(false);
      return response;
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: [
      "activities",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: findActivities,
    placeholderData: (previous) => previous?.data,
  });

  const debouncedSetFilter = useMemo(
    () =>
      debounce((val: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGlobalFilter(val);
      }, 200),
    []
  );

  const onPaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  const getPageCount = () => {
    const total = query.data?.meta?.itemCount ?? 0;
    return Math.ceil(total / pagination.pageSize);
  };

  const onRowViewAction = (row: IActivity) => {
    setSelectedActivity([row]);
    if(selectedActivity){
    navigate("/services/settings/home/activity", { state: { activity: row } });
    }
  };

  const onExportAction = () => {
    UtilService.exportToExcel(query?.data?.data, "activities");
  };

  const columns = useMemo<ColumnDef<IActivity>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "os",
        header: "OS",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "device",
        header: "Device",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "userAgent",
        header: "User Agent",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "method",
        header: "Method",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <div className="">
      <div className="relative">
        <GenericTable
          isLoading={isLoading}
          data={query?.data?.data ?? []}
          meta={query?.data?.meta ?? 0}
          columns={columns}
          globalFilter={globalFilter}
          onGlobalFilterChange={debouncedSetFilter}
          pageCount={getPageCount()}
          onPaginationChange={onPaginationChange}
          onRowSelectionChange={setSelectedActivity}
          onMore={onRowViewAction}
          actionButtons={<TableActionButtons onExport={onExportAction} />}
        />
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </div>
  );
};
